import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { homepageTestimonialsQuery } from "@/api-queries/homepage-testimonials";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { defineSection } from "@/ssr/define-section";
import { Marquee } from "@/components/ui/marquee";

interface ReviewCardProps {
  name: string;
  quote: string;
  rating: number;
}

export interface ReviewCardData extends ReviewCardProps {
  id: string;
}

function ReviewCard({ name, quote, rating }: ReviewCardProps) {
  return (
    <figure
      className="relative flex h-64 w-80 shrink-0 flex-col overflow-hidden rounded-2xl border-[1.5px] p-6"
      style={{
        background: "white",
        borderColor: "oklch(86% 0.03 84)",
      }}
    >
      <figcaption className="truncate text-sm font-extrabold tracking-[-0.005em]">
        {name}
      </figcaption>

      <div
        className="mt-3 inline-flex gap-0.5 text-sm tracking-[2px]"
        style={{ color: "oklch(74.72% 0.089 82.39)" }}
        aria-label={`${rating}/5`}
      >
        {Array.from({ length: 5 }, (_, i) => (
          <span key={i} style={{ opacity: i < rating ? 1 : 0.25 }}>
            ★
          </span>
        ))}
      </div>

      <blockquote className="mt-3 line-clamp-6 text-sm leading-relaxed text-foreground/80">
        {quote}
      </blockquote>
    </figure>
  );
}

export function TestimonialMarqueeRow({
  testimonials,
  reverse = false,
}: {
  testimonials: ReviewCardData[];
  reverse?: boolean;
}) {
  return (
    <Marquee reverse={reverse} pauseOnHover className="[--duration:400s]">
      {testimonials.map((tm) => (
        <ReviewCard key={tm.id} name={tm.name} quote={tm.quote} rating={tm.rating} />
      ))}
    </Marquee>
  );
}

function TestimonialsView() {
  const ref = useScrollReveal<HTMLElement>();
  const { t, i18n } = useTranslation();

  const { data } = useQuery(homepageTestimonialsQuery);
  const rows = data?.testimonials ?? [];

  const testimonials: ReviewCardData[] = rows.map((r) => ({
    id: r.id,
    name: r.name,
    quote: i18n.language === "en" ? r.quoteEn : r.quoteBs,
    rating: r.rating,
  }));

  if (testimonials.length === 0) return null;

  return (
    <section
      ref={ref}
      className="relative overflow-hidden py-24 sm:py-32"
      style={{ background: "oklch(95.43% 0.0136 78.26)" }}
    >
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center">
          <h2
            className="font-black leading-[0.95] tracking-[-0.035em] text-foreground"
            style={{
              fontFamily: "var(--font-heading)",
              fontSize: "clamp(2.25rem, 4.5vw, 3.5rem)",
            }}
          >
            {t("home.testimonials.titlePrefix")}{" "}
            <em
              className="font-normal italic"
              style={{
                fontFamily: "var(--font-heading)",
                color: "oklch(37.12% 0.059 186.62)",
              }}
            >
              {t("home.testimonials.titleHighlight")}
            </em>
          </h2>
        </div>
      </div>

      {/* Single marquee row */}
      <div className="relative mt-14 w-full">
        <TestimonialMarqueeRow testimonials={testimonials} />

        {/* Edge fades */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-1/4"
          style={{ background: "linear-gradient(to right, oklch(95.43% 0.0136 78.26), transparent)" }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-1/4"
          style={{ background: "linear-gradient(to left, oklch(95.43% 0.0136 78.26), transparent)" }}
        />
      </div>
    </section>
  );
}

export const Testimonials = defineSection({
  Component: TestimonialsView,
  queries: [homepageTestimonialsQuery],
});
